import { StockEntry } from "./Heatmap";

const fmt = (n?: number, decimals = 2) => {
  if (n === undefined || n === null) return "—";
  return Number(n).toFixed(decimals);
};

const fmtVolume = (v?: number): string => {
  if (!v) return "0";
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(2) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(1) + "K";
  return v.toLocaleString();
};

const getColor = (change: number): string => {
  if (change >= 5) return "#0d5c2e";
  if (change >= 3) return "#166634";
  if (change >= 1.5) return "#15803d";
  if (change >= 0.5) return "#16a34a";
  if (change > 0) return "#22c55e";
  if (change === 0) return "#374151";
  if (change >= -0.5) return "#b91c1c";
  if (change >= -1.5) return "#991b1b";
  if (change >= -3) return "#7f1d1d";
  if (change >= -10) return "#6b1a1a";
  return "#450a0a";
};

interface TooltipTheme {
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  tooltipMuted: string;
  divider: string;
}

export const buildTooltip = (
  s: StockEntry,
  theme: TooltipTheme,
  isDark: boolean
): string => {
  const chgColor = s.change >= 0 ? "#16a34a" : "#dc2626";
  const chgSign = s.change >= 0 ? "+" : "";
  const hasExtra = s.closed !== undefined;

  return `
    <div style="
      background:var(--vi-glass-tooltip-fill);
      border:1px solid ${theme.tooltipBorder};
      border-left:3px solid ${getColor(s.change)};
      backdrop-filter:var(--vi-glass-blur-overlay);
      -webkit-backdrop-filter:var(--vi-glass-blur-overlay);
      box-shadow:var(--vi-glass-tooltip-shadow);
      padding:10px 14px;
      font-family:'DM Mono',monospace;
      font-size:12px;
      color:${theme.tooltipText};
      border-radius:6px;
      min-width:200px;
      line-height:1.7;
    ">
      <div style="font-size:15px;font-weight:700;color:${
        theme.tooltipText
      };margin-bottom:2px">
        ${s.symbol}
      </div>
      ${
        s.company_name
          ? `<div style="color:${theme.tooltipMuted};font-size:10px;">${s.company_name}</div>`
          : ""
      }
      ${
        s.sector_name
          ? `<div style="
              display:inline-block;
              background:color-mix(in srgb, var(--vi-accent) 18%, transparent);
              color:var(--vi-accent);
              font-size:9px;
              padding:1px 7px;
              border-radius:10px;
              margin:4px 0 6px;
              border:1px solid color-mix(in srgb, var(--vi-accent) 40%, transparent);
            ">${s.sector_name}</div>`
          : ""
      }
      <div style="color:${chgColor};font-weight:600;margin-bottom:4px">
        ${chgSign}${fmt(s.change)}%
      </div>
      ${
        hasExtra
          ? `<div style="
              border-top:1px solid ${theme.divider};
              padding-top:6px;
              display:grid;
              grid-template-columns:1fr 1fr;
              gap:2px 12px;
              font-size:11px;
            ">
              <span style="color:${theme.tooltipMuted}">Last Traded Date</span>
              <span style="color:${theme.tooltipText};text-align:right">${
              s.last_traded_date
            }</span>
              <span style="color:${theme.tooltipMuted}">Close</span>
              <span style="color:${theme.tooltipText};text-align:right">${fmt(
              s.closed
            )}</span>
              <span style="color:${theme.tooltipMuted}">Open</span>
              <span style="color:${theme.tooltipText};text-align:right">${fmt(
              s.open
            )}</span>
              <span style="color:#16a34a">High</span>
              <span style="color:#16a34a;text-align:right">${fmt(s.high)}</span>
              <span style="color:#dc2626">Low</span>
              <span style="color:#dc2626;text-align:right">${fmt(s.low)}</span>
              <span style="color:${theme.tooltipMuted}">Volume</span>
              <span style="color:${
                theme.tooltipText
              };text-align:right">${fmtVolume(s.volume)}</span>
              <span style="color:${theme.tooltipMuted}">Trades</span>
              <span style="color:${theme.tooltipText};text-align:right">${(
              s.total_trades ?? 0
            ).toLocaleString()}</span>
            </div>`
          : `<div style="font-size:11px;color:${
              theme.tooltipMuted
            };margin-top:4px">
              Trades: ${(s.total_trades ?? 0).toLocaleString()}
            </div>`
      }
    </div>`;
};
